import { useState } from "react";
import {
  AlertCircle, CheckCircle2, Circle, CloudUpload, Copy, ExternalLink,
  GitBranch, GitPullRequest, Loader2, X,
} from "lucide-react";
import { CommitInlineSkeleton } from "./shared";
import {
  useDefaultGitBranch,
  useSetDefaultGitBranch,
  useGitCommit,
} from "@/hooks/git";
import { useSalesforcePushStore } from "@/stores/salesforcePushStore";
import type { GitBranch as GitBranchType, GitRepo } from "@/services/git/types";

// ─── Branch Detail Pane ───────────────────────────────────────────────────────

export function BranchDetailPane({
  repo,
  branch,
  onClose,
  onCreatePr,
}: {
  repo: GitRepo;
  branch: GitBranchType;
  onClose: () => void;
  onCreatePr: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [commitMsg, setCommitMsg] = useState("");

  const { data: defaultBranchData } = useDefaultGitBranch();
  const { mutate: setDefault, isPending: settingDefault } = useSetDefaultGitBranch();
  const { data: commit, isLoading: commitLoading } = useGitCommit(repo.full_name, branch.sha);

  // Lives in a module-scope store (not component state) so an in-flight push
  // keeps polling and stays visible in the Activity dropdown even if this
  // pane unmounts — user switches tabs/routes, or picks a different branch.
  const pushOp = useSalesforcePushStore((s) => s.getPush(repo.full_name, branch.name));
  const startPush = useSalesforcePushStore((s) => s.startPush);
  const clearPush = useSalesforcePushStore((s) => s.clearPush);

  const workingBranchId = defaultBranchData?.id ?? null;
  const isWorking = branch.id !== null && branch.id === workingBranchId;
  const isDefault = branch.name === repo.default_branch;
  const pushStatus = pushOp?.status;
  const branchUrl = `${repo.html_url}/tree/${encodeURIComponent(branch.name)}`;

  const isPushInFlight = pushStatus === "pending" || pushStatus === "in_progress";

  function handleCopy() {
    navigator.clipboard.writeText(branch.name);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  function handleSetDefault() {
    if (branch.id === null) return;
    setDefault({ gitBranchId: isWorking ? null : branch.id });
  }

  function handlePush() {
    const trimmed = commitMsg.trim();
    setCommitMsg("");
    void startPush(repo.full_name, branch.name, trimmed || undefined);
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* ── Header ── */}
      <div className="flex shrink-0 items-center gap-3 border-b border-border px-5 py-3.5">
        <GitBranch className="h-4 w-4 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-mono text-sm font-semibold">{branch.name}</span>
            {isDefault && (
              <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold text-primary">
                default
              </span>
            )}
            {isWorking && (
              <span className="shrink-0 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold text-emerald-400">
                working
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={handleCopy}
            title="Copy branch name"
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
          >
            {copied
              ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
              : <Copy className="h-3.5 w-3.5" />}
          </button>
          <a
            href={branchUrl}
            target="_blank"
            rel="noreferrer"
            title="View on GitHub"
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div className="flex-1 overflow-y-auto">

        {/* Latest commit */}
        <section className="border-b border-border/60 px-5 py-4">
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Latest commit
          </p>
          {commitLoading ? (
            <CommitInlineSkeleton />
          ) : commit ? (
            <div className="space-y-1.5">
              <p className="text-sm font-medium leading-snug">{commit.message.split("\n")[0]}</p>
              <p className="flex flex-wrap items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
                <span className="text-primary/80">{commit.sha.slice(0, 7)}</span>
                <span>·</span>
                <span>{commit.author_name}</span>
                {commit.committed_at && (
                  <>
                    <span>·</span>
                    <span>{new Date(commit.committed_at).toLocaleDateString()}</span>
                  </>
                )}
              </p>
            </div>
          ) : (
            <p className="font-mono text-[10px] text-muted-foreground">{branch.sha.slice(0, 7)}</p>
          )}
        </section>

        {/* Actions */}
        <section className="border-b border-border/60 px-5 py-4">
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Actions
          </p>
          <div className="space-y-2">
            <button
              type="button"
              disabled={settingDefault || branch.id === null}
              onClick={handleSetDefault}
              className={[
                "flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left text-xs font-medium transition-colors disabled:opacity-50",
                isWorking
                  ? "border-emerald-500/30 bg-emerald-500/8 text-emerald-400 hover:bg-emerald-500/12"
                  : "border-border/60 bg-muted/20 text-foreground hover:bg-muted/40",
              ].join(" ")}
            >
              {settingDefault
                ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                : isWorking
                ? <CheckCircle2 className="h-4 w-4 shrink-0" />
                : <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />}
              <div>
                <p>{isWorking ? "Working branch · click to clear" : "Set as working branch"}</p>
                <p className="mt-0.5 text-[10px] font-normal text-muted-foreground">
                  {isWorking
                    ? "Used for pushes and deployments"
                    : "Use this branch for pushes and deployments"}
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={onCreatePr}
              className="flex w-full items-center gap-3 rounded-lg border border-border/60 bg-muted/20 px-4 py-3 text-left text-xs font-medium transition-colors hover:bg-muted/40"
            >
              <GitPullRequest className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div>
                <p>Create pull request</p>
                <p className="mt-0.5 text-[10px] font-normal text-muted-foreground">
                  Open a PR from this branch
                </p>
              </div>
            </button>
          </div>
        </section>

        {/* Sync to GitHub */}
        <section className="px-5 py-4">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Sync to GitHub
          </p>
          <p className="mb-4 text-[10px] text-muted-foreground/60">
            Push all Salesforce metadata from this org to this branch.
          </p>

          {/* Status feedback */}
          {pushOp && pushStatus === "in_progress" && (
            <div className="mb-3 overflow-hidden rounded-lg border border-border/60 bg-muted/20">
              <div className="flex items-center justify-between px-3 py-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Pushing files…</span>
                </div>
                <span className="font-mono text-[10px] text-muted-foreground">
                  {pushOp.filesPushed} / {pushOp.filesTotal}
                </span>
              </div>
              {pushOp.filesTotal > 0 && (
                <div className="h-1 bg-muted/40">
                  <div
                    className="h-full rounded-full bg-primary/60 transition-all duration-500"
                    style={{ width: `${Math.round((pushOp.filesPushed / pushOp.filesTotal) * 100)}%` }}
                  />
                </div>
              )}
            </div>
          )}

          {pushOp && pushStatus === "pending" && (
            <div className="mb-3 flex items-center gap-2 rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Preparing push…
            </div>
          )}

          {pushOp && pushStatus === "completed" && (
            <div className="mb-3 flex items-center gap-2 rounded-lg border border-emerald-500/25 bg-emerald-500/8 px-3 py-2.5 text-xs text-emerald-400">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1">
                Pushed {pushOp.filesPushed} file{pushOp.filesPushed !== 1 ? "s" : ""}
                {pushOp.commitSha && <> · <span className="font-mono">{pushOp.commitSha.slice(0, 7)}</span></>}
              </span>
              {pushOp.commitUrl && (
                <a href={pushOp.commitUrl} target="_blank" rel="noreferrer" className="shrink-0 hover:text-emerald-300">
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
              <button type="button" onClick={() => clearPush(repo.full_name, branch.name)} className="shrink-0 hover:text-emerald-300">
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {pushOp && pushStatus === "failed" && (
            <div className="mb-3 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1">{pushOp.error ?? "Push failed"}</span>
              <button type="button" onClick={() => clearPush(repo.full_name, branch.name)}>
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {/* Commit message + push button */}
          <div className="space-y-2">
            <input
              type="text"
              value={commitMsg}
              onChange={(e) => setCommitMsg(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !isPushInFlight && handlePush()}
              placeholder="Commit message (optional)"
              disabled={isPushInFlight}
              className="w-full rounded-lg border border-border bg-muted/20 px-3 py-2 text-xs outline-none placeholder:text-muted-foreground/40 transition-colors focus:border-primary/60 focus:bg-background disabled:opacity-50"
            />
            <button
              type="button"
              disabled={isPushInFlight}
              onClick={handlePush}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/8 px-4 py-2.5 text-xs font-medium text-emerald-400 transition-colors hover:bg-emerald-500/12 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isPushInFlight
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <CloudUpload className="h-3.5 w-3.5" />}
              {pushStatus === "pending" ? "Initiating…" : isPushInFlight ? "Pushing…" : "Push Salesforce Metadata"}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
